import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export type GrowthTask = {
  id: string;
  created_at: string;
  title: string;
  description: string;
  impact: 'high' | 'med' | 'low';
  status: 'todo' | 'doing' | 'done' | 'snoozed';
  due_date: string | null;
  owner_user_id: string | null;
  evidence: unknown[];
  source: 'audit' | 'brief' | 'manual';
};

async function fetchTasks(): Promise<GrowthTask[]> {
  const { data, error } = await supabase
    .from('growth_tasks')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as GrowthTask[];
}

export function useGrowthTasks() {
  return useQuery({
    queryKey: ['growth', 'tasks'],
    queryFn: fetchTasks,
  });
}

export function useGrowthTaskUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Pick<GrowthTask, 'status' | 'title' | 'description' | 'due_date' | 'owner_user_id'>>;
    }) => {
      const { data, error } = await supabase
        .from('growth_tasks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['growth', 'tasks'] }),
  });
}

export function useGrowthTaskCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (task: {
      title: string;
      description: string;
      impact: GrowthTask['impact'];
      source: GrowthTask['source'];
      evidence?: unknown[];
    }) => {
      const { data, error } = await supabase.from('growth_tasks').insert(task).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['growth', 'tasks'] }),
  });
}
